import { useCallback, useEffect, useId, useRef, useState } from 'react';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { GeneratedIntakeGroupHelp } from '../../types';
import type { IntakeDocumentDetail } from '../intakeDocumentDetail';
import { IntakeDocumentDetailDialog } from '../IntakeDocumentDetailDialog';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import { useGroupHelpPanel } from './useGroupHelpPanel';

/**
 * Opens a blank tab synchronously so it inherits the current user activation.
 * `noopener` is deliberately omitted here — it makes `window.open` return null
 * in Chromium, which is the very handle we need — so the opener link is severed
 * by hand instead.
 */
function openBlankTab(): Window | null {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  return tab;
}

function getFileType(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension && extension !== filename.toLowerCase() ? extension : 'unknown';
}

export interface FoundDocument {
  artifactId: string | null;
  filename: string;
  status: string;
  docKind: string | null;
  filledCount: number;
  understood: boolean;
  detail?: IntakeDocumentDetail;
}

/** Per-required-document satisfaction status shown in the "What to upload" list. */
export interface DocumentRequirementStatus {
  key: string;
  label: string;
  satisfied: boolean;
  satisfiedByFilename?: string;
  /** The uploaded document that satisfied this requirement, if resolvable — lets the row open the same preview dialog the "Documents received" list uses. */
  satisfiedDocument?: FoundDocument;
  /** Canvas group this requirement belongs to, so the row can carry that group's help affordance. */
  groupId?: string;
}

/**
 * Plain-words caption for an upload's lifecycle. `status` is the collapsed
 * display status, not the raw backend `processing_status` — never print the
 * raw value at a user.
 */
function documentStatusLabel(status: string): string {
  switch (status) {
    case 'failed':
      return 'Upload failed';
    case 'parsing':
      return 'Parsing…';
    case 'uploading':
      return 'Uploading…';
    default:
      return 'Ready';
  }
}

/** "What to upload" box. Satisfied requirements go green so the list doubles as a progress read. */
function requirementBoxSx(satisfied: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    border: `1px solid ${satisfied ? wf.green : wf.lineSoft}`,
    background: satisfied ? wf.greenSoft : 'transparent',
    color: satisfied ? wf.green : 'inherit',
    borderRadius: '6px',
    px: 1.25,
    py: 0.75,
    fontSize: '12.5px',
    textAlign: 'left' as const,
    minWidth: 0,
  };
}

/** Applied to the clickable target inside a satisfied box, not to the box itself. */
const interactiveRequirementTargetSx = {
  font: 'inherit',
  fontSize: '12.5px',
  color: 'inherit',
  background: 'transparent',
  border: 0,
  padding: 0,
  textAlign: 'left' as const,
  cursor: 'pointer',
  borderRadius: '4px',
  transition: 'box-shadow 120ms ease',
  '&:hover': { boxShadow: `0 0 0 1px ${wf.green}` },
  '&:focus-visible': { outline: `2px solid ${wf.blue}`, outlineOffset: '2px' },
} as const;

/**
 * `chipSx('ok')` is greenSoft on a transparent border, which disappears against a
 * satisfied box's greenSoft background. Give the chip a white ground and a real
 * green border there without changing the token for its other callers.
 */
const satisfiedChipSx = {
  background: wf.surface,
  border: `1px solid ${wf.green}`,
} as const;

function documentStatusTone(status: string): 'rev' | 'pend' | 'ok' {
  if (status === 'failed') return 'rev';
  if (status === 'parsing' || status === 'uploading') return 'pend';
  return 'ok';
}

interface IntakeDocumentsPageProps {
  title: string;
  sub: string;
  dropHint: string;
  documents: FoundDocument[];
  isDragging: boolean;
  isProcessing?: boolean;
  onFiles: (files: File[]) => void;
  onContinue: () => void;
  onBack?: () => void;
  nextLabel: string;
  /** Plain-words summary of where document suggestions can be reviewed. */
  landedSummary?: string | null;
  onReview?: () => void;
  reviewLabel?: string;
  groups?: GeneratedIntakeGroupHelp[];
  onHelpAction?: (action: 'message_specialist') => void;
  getDocumentUrl?: (artifactId: string) => Promise<string>;
  onDismissFailed?: (artifactId: string) => void;
  embedded?: boolean;
  missingRequiredDocumentCount?: number;
  readOnly?: boolean;
  /** Per-required-document satisfaction, driven by the backend's `is_complete`. */
  requirements?: DocumentRequirementStatus[];
  /** Files whose upload request is still in flight — rendered as "Uploading…" placeholders. */
  uploadingCount?: number;
  /** Admin surfaces get a per-row control that opens the raw file via the signed URL. */
  showRowDownload?: boolean;
}

export function IntakeDocumentsPage({
  title,
  sub,
  dropHint,
  documents,
  isDragging,
  isProcessing = false,
  onFiles,
  onContinue,
  onBack,
  nextLabel,
  landedSummary,
  onReview,
  reviewLabel,
  groups = [],
  onHelpAction = () => undefined,
  getDocumentUrl: resolveDocumentUrl,
  onDismissFailed,
  embedded = false,
  missingRequiredDocumentCount = 0,
  readOnly = false,
  requirements = [],
  uploadingCount = 0,
  showRowDownload = false,
}: IntakeDocumentsPageProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const { intakeHub, error: trackError } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDocument, setSelectedDocument] = useState<FoundDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = `intake-document-group-help-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '');
    folderInputRef.current?.setAttribute('directory', '');
  }, []);

  const getDocumentUrl = useCallback(
    async (document: FoundDocument): Promise<string | null> => {
      if (!document.artifactId) return null;
      try {
        if (!resolveDocumentUrl) return null;
        return await resolveDocumentUrl(document.artifactId);
      } catch {
        trackError.track('artifact_url_request_failed', 'intake_hub_document_viewer', {
          artifact_id: document.artifactId,
          file_type: getFileType(document.filename),
        });
        return null;
      }
    },
    [resolveDocumentUrl, trackError],
  );

  const handleOpenDocument = useCallback(
    async (document: FoundDocument) => {
      setSelectedDocument(document);
      setPreviewUrl(null);
      intakeHub.documentPreviewOpened({
        artifact_id: document.artifactId ?? 'unavailable',
        file_type: getFileType(document.filename),
      });

      if (!document.artifactId) {
        return;
      }

      setIsPreviewLoading(true);
      const url = await getDocumentUrl(document);
      setPreviewUrl(url);
      setIsPreviewLoading(false);
    },
    [getDocumentUrl, intakeHub],
  );

  /**
   * Opens a document's signed URL in a new tab. The tab is created up front,
   * inside the click's own user activation, because the URL has to be fetched
   * first and Safari (and strict popup blockers) refuse a `window.open` that
   * happens after an await. On failure the placeholder is closed and the user
   * is told, rather than being left with a blank tab and no explanation.
   */
  const openDocumentInNewTab = useCallback(
    async (document: FoundDocument, knownUrl?: string | null) => {
      const tab = openBlankTab();
      const url = knownUrl ?? (await getDocumentUrl(document));
      if (!url) {
        tab?.close();
        enqueueSnackbar('Could not open the document. Try again.', { variant: 'error' });
        return;
      }
      if (tab) {
        tab.location.href = url;
        return;
      }
      // Same caveat as openBlankTab: a noopener feature string would make
      // window.open return null even on success, so open bare, sever the
      // opener by hand, and treat a null handle as a genuine block.
      const fallbackTab = window.open(url, '_blank');
      if (fallbackTab) {
        fallbackTab.opener = null;
        return;
      }
      enqueueSnackbar('Your browser blocked the document tab. Allow pop-ups and try again.', {
        variant: 'error',
      });
    },
    [enqueueSnackbar, getDocumentUrl],
  );

  const handleOpenFile = useCallback(
    async (artifactId?: string | null) => {
      if (!artifactId || !selectedDocument) return;
      intakeHub.documentDownloadClicked({
        artifact_id: artifactId,
        file_type: getFileType(selectedDocument.filename),
      });
      await openDocumentInNewTab(selectedDocument, previewUrl);
    },
    [intakeHub, openDocumentInNewTab, previewUrl, selectedDocument],
  );

  /**
   * Row-level open for admin surfaces. Reuses the same signed-URL resolver the
   * preview dialog uses, so no extra endpoint and no extra permission surface.
   */
  const handleDownloadDocument = useCallback(
    async (document: FoundDocument) => {
      if (!document.artifactId) return;
      intakeHub.documentDownloadClicked({
        artifact_id: document.artifactId,
        file_type: getFileType(document.filename),
      });
      await openDocumentInNewTab(document);
    },
    [intakeHub, openDocumentInNewTab],
  );

  /**
   * Canvas groups no required-document requirement speaks for — an optional or
   * trailing-gate upload slot. They still belong in "What to upload" (with their
   * help panel), they just never turn green.
   */
  const uncoveredGroups = groups.filter(
    group => !requirements.some(requirement => requirement.groupId === group.group_id),
  );

  const closeDocument = () => {
    setSelectedDocument(null);
    setPreviewUrl(null);
  };

  const selectedDetail: IntakeDocumentDetail | null = selectedDocument
    ? (selectedDocument.detail ?? {
        id: selectedDocument.artifactId ?? selectedDocument.filename,
        sourceArtifactId: selectedDocument.artifactId,
        title: selectedDocument.filename,
        docKind: selectedDocument.docKind,
      })
    : null;

  return (
    <>
      {!embedded && <IntakePageHeader title={title} sub={sub} />}

      {(requirements.length > 0 || groups.length > 0) && (
        <Box sx={cardSx} aria-label="What to upload">
          <Box sx={{ ...groupTitleSx, mb: 1 }}>What to upload</Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
            {requirements.map(requirement => {
              const group = requirement.groupId
                ? groups.find(candidate => candidate.group_id === requirement.groupId)
                : undefined;
              const satisfiedDocument = requirement.satisfiedDocument;
              const interactive = requirement.satisfied && Boolean(satisfiedDocument);
              return (
                <Box key={requirement.key} sx={requirementBoxSx(requirement.satisfied)}>
                  {/* The clickable target is this inner element, not the whole box, so the
                      group's help trigger can sit beside it rather than nested inside a button. */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flex: 1,
                      minWidth: 0,
                      ...(interactive ? interactiveRequirementTargetSx : null),
                    }}
                    {...(interactive
                      ? {
                          component: 'button' as const,
                          type: 'button' as const,
                          'aria-label': `View ${satisfiedDocument?.filename ?? requirement.label}`,
                          onClick: () => {
                            if (satisfiedDocument) void handleOpenDocument(satisfiedDocument);
                          },
                        }
                      : {})}
                  >
                    <Box
                      component="span"
                      sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: '2px' }}
                    >
                      <Box component="span">{requirement.label}</Box>
                      {requirement.satisfied && requirement.satisfiedByFilename && (
                        <Box
                          component="span"
                          sx={{
                            color: wf.green,
                            fontSize: '10.5px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Met by {requirement.satisfiedByFilename}
                        </Box>
                      )}
                    </Box>
                    <Box
                      component="span"
                      sx={
                        requirement.satisfied
                          ? { ...chipSx('ok'), ...satisfiedChipSx }
                          : chipSx('mut')
                      }
                    >
                      {requirement.satisfied ? 'Received' : 'Needed'}
                    </Box>
                  </Box>
                  {group?.help_panel && (
                    <GroupHelpTrigger
                      groupName={group.group_name}
                      controlsId={drawerId}
                      expanded={selectedGroupId === group.group_id}
                      onOpen={opener => openGroup(group.group_id, opener)}
                    />
                  )}
                </Box>
              );
            })}
            {uncoveredGroups.map(group => (
              <Box key={group.group_id} sx={requirementBoxSx(false)}>
                <span>{group.group_name}</span>
                {group.help_panel && (
                  <GroupHelpTrigger
                    groupName={group.group_name}
                    controlsId={drawerId}
                    expanded={selectedGroupId === group.group_id}
                    onOpen={opener => openGroup(group.group_id, opener)}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!readOnly && (
        <Box
          sx={{
            border: `2px dashed ${isDragging ? wf.blue : wf.line}`,
            borderRadius: '10px',
            padding: '26px',
            textAlign: 'center',
            color: wf.muted,
            fontSize: '13px',
            background: isDragging ? wf.blueSoft : wf.surfaceAlt,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
            onChange={event => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) onFiles(files);
              event.target.value = '';
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            data-testid="intake-folder-input"
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
            onChange={event => {
              const files = Array.from(event.target.files ?? []);
              if (files.length > 0) onFiles(files);
              event.target.value = '';
            }}
          />
          <Box>Drag documents or a folder here</Box>
          <Box sx={{ fontSize: '11.5px', marginTop: '4px' }}>{dropHint}</Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ justifyContent: 'center', mt: 1.5 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              Choose files
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => {
                intakeHub.folderUploadClicked({ surface: 'document_canvas' });
                folderInputRef.current?.click();
              }}
              sx={{ textTransform: 'none' }}
            >
              Choose folder
            </Button>
          </Stack>
        </Box>
      )}

      {embedded && missingRequiredDocumentCount > 0 && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem' }}>
          {missingRequiredDocumentCount === 1
            ? '1 required document is missing.'
            : `${missingRequiredDocumentCount} required documents are missing.`}
        </Box>
      )}

      {landedSummary && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            border: `1px solid ${wf.green}`,
            background: wf.greenSoft,
            color: wf.green,
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '12.5px',
          }}
        >
          <span>
            We found <strong>{landedSummary}</strong>. Review each value before moving on.
          </span>
          {onReview && reviewLabel && (
            <Box
              role="button"
              tabIndex={0}
              onClick={onReview}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onReview();
                }
              }}
              sx={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                borderRadius: '7px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 650,
                background: wf.green,
                color: '#fff',
              }}
            >
              {reviewLabel}
            </Box>
          )}
        </Box>
      )}

      {(documents.length > 0 || uploadingCount > 0) && (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Box sx={groupTitleSx}>Documents received</Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Array.from({ length: uploadingCount }, (_unused, index) => (
              <Box
                key={`uploading-${index}`}
                sx={{
                  width: '100%',
                  border: `1px solid ${wf.line}`,
                  borderRadius: '6px',
                  padding: '7px 10px',
                  minHeight: '34px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  fontSize: '12.5px',
                  color: wf.muted,
                }}
              >
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <CircularProgress size={12} thickness={5} aria-label="Uploading your file" />
                  <span>Uploading your file</span>
                </Stack>
                <Box sx={chipSx(documentStatusTone('uploading'))}>
                  {documentStatusLabel('uploading')}
                </Box>
              </Box>
            ))}
            {documents.map(document => {
              const artifactId = document.artifactId;

              return (
                <Box
                  key={`${document.artifactId ?? 'unavailable'}-${document.filename}`}
                  sx={{
                    width: '100%',
                    border: `1px solid ${wf.line}`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={`View ${document.filename}`}
                    onClick={() => void handleOpenDocument(document)}
                    sx={{
                      flex: 1,
                      textAlign: 'left',
                      color: 'inherit',
                      background: 'transparent',
                      border: 0,
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      minHeight: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 120ms ease, box-shadow 120ms ease',
                      '&:hover': { background: wf.surfaceAlt },
                      '&:focus-visible': {
                        outline: `2px solid ${wf.blue}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <Box component="span">
                      <Box
                        component="span"
                        sx={{ color: wf.muted, fontSize: '10.5px', display: 'block' }}
                      >
                        {document.filename}
                      </Box>
                      {document.status === 'failed' ? (
                        <Box component="span" sx={{ color: 'error.main' }}>
                          Upload failed. Try uploading this file again.
                        </Box>
                      ) : document.status === 'parsing' ? (
                        <Stack
                          component="span"
                          direction="row"
                          spacing={0.75}
                          sx={{ alignItems: 'center' }}
                        >
                          <CircularProgress
                            size={12}
                            thickness={5}
                            aria-label={`Reading ${document.filename}`}
                          />
                          <span>Reading this document</span>
                        </Stack>
                      ) : document.docKind ? (
                        document.docKind.replace(/_/g, ' ')
                      ) : document.understood ? (
                        'Uploaded'
                      ) : (
                        "We couldn't tell what this is"
                      )}
                    </Box>
                    {document.status !== 'failed' && !document.understood ? (
                      <Box sx={chipSx('rev')}>choose a slot</Box>
                    ) : (
                      <Box sx={chipSx(documentStatusTone(document.status))}>
                        {documentStatusLabel(document.status)}
                      </Box>
                    )}
                  </Box>
                  {showRowDownload && artifactId && document.status !== 'failed' && (
                    <Button
                      size="small"
                      startIcon={<FileDownloadOutlinedIcon />}
                      aria-label={`Download ${document.filename}`}
                      onClick={() => void handleDownloadDocument(document)}
                      sx={{ mr: 0.5, textTransform: 'none' }}
                    >
                      Download
                    </Button>
                  )}
                  {document.status === 'failed' && artifactId && onDismissFailed && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onDismissFailed(artifactId)}
                      sx={{ mr: 0.5, textTransform: 'none' }}
                    >
                      Dismiss
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {landedSummary && (
        <Box
          sx={{
            fontSize: '11.5px',
            color: wf.muted,
            background: wf.surfaceAlt,
            borderRadius: '6px',
            padding: '8px 10px',
          }}
        >
          Fields we fill from documents show as{' '}
          <Box component="span" sx={{ ...chipSx('sug'), verticalAlign: 'middle' }}>
            suggested
          </Box>{' '}
          — you confirm them on the next pages, or accept a whole page at once.
        </Box>
      )}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={onContinue}
          onBack={onBack}
          disabled={isProcessing}
          disabledReason={
            isProcessing
              ? 'Wait for your documents to finish processing before continuing.'
              : undefined
          }
        />
      )}
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
      <IntakeDocumentDetailDialog
        open={Boolean(selectedDocument)}
        document={selectedDetail}
        previewUrl={previewUrl}
        isPreviewLoading={isPreviewLoading}
        onOpenFile={handleOpenFile}
        onClose={closeDocument}
      />
    </>
  );
}
